import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@neo/primitives/alert-dialog'
import { Button } from '@neo/primitives/button'
import { useMessageScroller } from '@neo/primitives/message-scroller'
import { Popover, PopoverAnchor } from '@neo/primitives/popover'
import { AnimatePresence, m } from 'framer-motion'
import { CornerDownLeft, ListPlus, Loader2, Square } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { DURATION, EASE_OUT } from '../motion'
import { ensureNotifyPermission } from '../notify'
import { selectIsRunActive, useVibeAgentStore } from '../store'
import {
  type NodeMentionData,
  VibeEnhancedInput,
  type VibeEnhancedInputRef,
} from './enhanced-input'
import { NodeMentionPopover } from './node-mention-popover'
import { VibeQueueStack } from './queue-stack'
import { useVibeAgentApi } from './use-vibe-agent-api'

export const VibeInput = () => {
  const userInput = useVibeAgentStore((state) => state.userInput)
  const setUserInput = useVibeAgentStore((state) => state.setUserInput)
  const isRunActive = useVibeAgentStore(selectIsRunActive)
  const gate = useVibeAgentStore((state) => state.gate)
  const enqueueMessage = useVibeAgentStore((state) => state.enqueueMessage)
  const queueCount = useVibeAgentStore((state) => state.queuedMessages.length)
  const queuePaused = useVibeAgentStore((state) => state.queuePaused)
  const clearQueue = useVibeAgentStore((state) => state.clearQueue)
  // After the agent has spoken once, the empty composer invites iteration,
  // not a from-scratch build description.
  const hasAgentActivity = useVibeAgentStore((state) =>
    state.transcript.some((e) => e.role !== 'user'),
  )
  // Cancel needs the run's response_id (arrives with `started` / the snapshot);
  // during the pre-started 'sending' window Stop would be a silent no-op.
  const canCancel = useVibeAgentStore((state) => !!state.responseId)
  const { send, cancel } = useVibeAgentApi()
  const { scrollToEnd } = useMessageScroller()
  const [showMentionPopover, setShowMentionPopover] = useState(false)
  const [confirmPausedSend, setConfirmPausedSend] = useState(false)
  // Codex-style blocked-submit feedback: clicking a disabled action explains
  // itself in a transient tip instead of doing nothing.
  const [blockedTip, setBlockedTip] = useState<string | null>(null)
  const blockedTipTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inputRef = useRef<VibeEnhancedInputRef>(null)

  useEffect(
    () => () => {
      if (blockedTipTimer.current) clearTimeout(blockedTipTimer.current)
    },
    [],
  )

  const flashBlockedTip = (message: string) => {
    setBlockedTip(message)
    if (blockedTipTimer.current) clearTimeout(blockedTipTimer.current)
    blockedTipTimer.current = setTimeout(() => setBlockedTip(null), 1600)
  }

  const dispatchSend = () => {
    const text = inputRef.current?.getPlainText() || userInput
    send(text)
    inputRef.current?.clear()
    setUserInput('')
    // Sending is explicit — always bring the sent message into view, even if
    // the reader had scrolled up. rAF so the optimistic row is painted first.
    // Deliberately NOT done for queue drains: an auto-send must never yank
    // the scroll position while the user is reading history.
    requestAnimationFrame(() => scrollToEnd({ behavior: 'smooth' }))
  }

  const onSendMessage = () => {
    const text = inputRef.current?.getPlainText() || userInput
    if (!text.trim()) return
    ensureNotifyPermission()
    // A fresh send while the queue is paused silently strands (or later
    // surprises with) the held messages — make the user decide first.
    if (queuePaused && queueCount > 0) {
      setConfirmPausedSend(true)
      return
    }
    dispatchSend()
  }

  // While a run is busy the composer stays typeable and Enter STAGES the
  // message instead of sending — it dispatches when the run completes.
  // Gates never reach this branch: isRunActive is false whenever a gate is
  // armed, so typing during a gate stays the answer path.
  const onQueueMessage = () => {
    const text = inputRef.current?.getPlainText() || userInput
    if (!text.trim()) return
    enqueueMessage(text)
    inputRef.current?.clear()
    setUserInput('')
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !showMentionPopover) {
      e.preventDefault()
      if (isRunActive) onQueueMessage()
      else onSendMessage()
    }
  }

  const handleMentionSelect = (node: NodeMentionData) => {
    inputRef.current?.insertMention(node)
    setShowMentionPopover(false)
    inputRef.current?.focus()
  }

  const hasText = !!userInput.trim()
  // Codex morph rule: the primary slot is Stop only while the run is busy AND
  // the composer is empty; typing flips it to Queue, idle means Send.
  const buttonMode: 'stop' | 'queue' | 'send' = isRunActive ? (hasText ? 'queue' : 'stop') : 'send'

  const placeholder = gate
    ? 'Type your answer, or pick an option above…'
    : isRunActive
      ? 'Queue a message. Sends when the agent is free…'
      : hasAgentActivity
        ? 'Ask for follow-up changes…'
        : 'Describe the agentic flow you want to build…'

  return (
    <div className="mt-auto relative rounded-xl">
      <Popover open={showMentionPopover} onOpenChange={setShowMentionPopover}>
        <PopoverAnchor asChild>
          {/* The send/stop button lives in a footer ROW inside the bordered box —
              a floating corner overlay forced a full-height pr-12 gutter on the
              editor (visible as a dead right strip once content scrolled). */}
          <div className="relative">
            <VibeQueueStack />
            <AnimatePresence>
              {isRunActive && (
                <m.div
                  initial={{ opacity: 0, transform: 'translateY(10px)' }}
                  animate={{ opacity: 1, transform: 'translateY(0px)' }}
                  exit={{
                    opacity: 0,
                    transform: 'translateY(10px)',
                    transition: { duration: DURATION.exit, ease: EASE_OUT },
                  }}
                  transition={{ duration: DURATION.panel, ease: EASE_OUT }}
                  className="relative z-[5] mx-[2px] rounded-t-2xl bg-bg2 -mb-4"
                >
                  <div className="flex items-start justify-between px-3 pt-2 pb-5">
                    <span className="text-p-400">Generating…</span>
                    <Loader2 className="w-4 h-4 animate-spin ml-auto" />
                  </div>
                </m.div>
              )}
            </AnimatePresence>
            {/* Flex column so a manual resize-y drag stretches the editor while
                the button row stays pinned to the bottom; min/max clamp the drag
                and double as the auto-grow cap while typing. */}
            <div className="relative z-10 flex flex-col min-h-[140px] max-h-[400px] rounded-2xl border border-st1 bg-background resize-y overflow-hidden transition-[border-color,box-shadow] duration-150 focus-within:border-primary/40 focus-within:ring-2 focus-within:ring-primary/15">
              <VibeEnhancedInput
                ref={inputRef}
                value={userInput}
                onChange={setUserInput}
                onMentionTrigger={setShowMentionPopover}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                className="border-0 shadow-none resize-none flex-1 min-h-0"
              />
              <div className="relative flex justify-end items-center gap-2.5 px-3 pb-2 shrink-0">
                <AnimatePresence>
                  {blockedTip && (
                    <m.div
                      initial={{ opacity: 0, transform: 'translateY(4px)' }}
                      animate={{ opacity: 1, transform: 'translateY(0px)' }}
                      exit={{
                        opacity: 0,
                        transition: { duration: DURATION.exitFast, ease: EASE_OUT },
                      }}
                      transition={{ duration: DURATION.minor, ease: EASE_OUT }}
                      className="absolute bottom-full right-3 mb-1.5 z-20 rounded-md bg-foreground text-background text-xs-500 px-2 py-1 shadow-md whitespace-nowrap"
                      role="status"
                      data-testid="tip-blocked-submit"
                    >
                      {blockedTip}
                    </m.div>
                  )}
                </AnimatePresence>
                {buttonMode === 'stop' && (
                  <span
                    onPointerDown={() => {
                      if (!canCancel) flashBlockedTip('Hold on, the run is still starting')
                    }}
                  >
                    <Button
                      type="button"
                      size="icon"
                      onClick={cancel}
                      disabled={!canCancel}
                      aria-label="Stop generating"
                      className="h-7 w-7 rounded-md bg-primary text-primary-foreground hover:bg-primary/80 shadow-none transition-transform duration-150 active:scale-[0.97]"
                      data-testid="button-stop-generating"
                    >
                      <Square className="w-3 h-3 fill-current" />
                    </Button>
                  </span>
                )}
                {buttonMode === 'queue' && (
                  <Button
                    type="button"
                    size="icon"
                    onClick={onQueueMessage}
                    aria-label="Queue message. Sends when the agent is free"
                    className="h-7 w-7 bg-background text-foreground hover:bg-background/80 shadow-none transition-transform duration-150 active:scale-[0.97]"
                    data-testid="button-queue-message"
                  >
                    <ListPlus className="w-3.5 h-3.5" />
                  </Button>
                )}
                {buttonMode === 'send' && (
                  <span
                    onPointerDown={() => {
                      if (!hasText) {
                        flashBlockedTip(gate ? 'Type your answer first' : 'Type a message first')
                      }
                    }}
                  >
                    <Button
                      type="button"
                      size="icon"
                      onClick={onSendMessage}
                      disabled={!hasText}
                      aria-label="Send message"
                      className="h-7 w-7 bg-background text-foreground hover:bg-background/80 shadow-none transition-transform duration-150 active:scale-[0.97]"
                      data-testid="button-send-message"
                    >
                      <CornerDownLeft className="w-3.5 h-3.5" />
                    </Button>
                  </span>
                )}
              </div>
            </div>
          </div>
        </PopoverAnchor>
        <NodeMentionPopover
          onSelect={handleMentionSelect}
          onClose={() => setShowMentionPopover(false)}
        />
      </Popover>

      <AlertDialog open={confirmPausedSend} onOpenChange={setConfirmPausedSend}>
        <AlertDialogContent data-testid="dialog-paused-queue-send">
          <AlertDialogHeader>
            <AlertDialogTitle>Send message?</AlertDialogTitle>
            <AlertDialogDescription>
              {queueCount === 1
                ? '1 queued message is on hold from the stopped run.'
                : `${queueCount} queued messages are on hold from the stopped run.`}{' '}
              What should happen to {queueCount === 1 ? 'it' : 'them'}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmPausedSend(false)}
              data-testid="button-paused-send-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmPausedSend(false)
                dispatchSend()
              }}
              data-testid="button-paused-send-keep"
            >
              Keep {queueCount === 1 ? 'it' : 'them'} & send
            </Button>
            <Button
              onClick={() => {
                clearQueue()
                setConfirmPausedSend(false)
                dispatchSend()
              }}
              data-testid="button-paused-send-clear"
            >
              Clear {queueCount === 1 ? 'it' : 'them'} & send
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
